import React from "react";
import Container from "Components/Container/Container";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  ArrowRight,
} from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";

// Instructor block — one consistent card across every breakpoint, matching
// the site's `grey` gradient card language (Contact page, etc.) instead of
// floating bare on desktop.
const Instructor = ({ data }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const imageUrl = data?.profileImage
    ? `${BASE_URL.replace(/\/api$/, "")}/${data.profileImage}`
    : null;

  const socials = [
    data?.facebook && { href: data.facebook, Icon: FacebookLogo, label: "Facebook" },
    data?.instagram && { href: data.instagram, Icon: InstagramLogo, label: "Instagram" },
    data?.linkedin && { href: data.linkedin, Icon: LinkedinLogo, label: "LinkedIn" },
  ].filter(Boolean);

  if (!data) return null;

  return (
    <section className="pt-10 sm:pt-14 lg:pt-primary">
      <Container>
        <div
          className="
            bg-gradient-to-br from-grey to-grey/50 rounded-2xl shadow-xl
            border border-gray-800 p-6 sm:p-8 lg:p-10
          "
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 lg:gap-10">
            {/* Avatar */}
            <div className="flex items-center gap-4 sm:block shrink-0">
              <div className="w-20 h-20 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden ring-2 ring-primary/30 flex-shrink-0">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={data?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-lightGrey flex items-center justify-center text-white font-bold text-2xl">
                    {data?.name?.charAt(0) || "I"}
                  </div>
                )}
              </div>

              {/* Mobile-only: name sits next to the avatar for a tighter header row */}
              <div className="flex-1 min-w-0 sm:hidden">
                <p className="text-primary text-xs uppercase tracking-[0.14em] font-semibold">
                  {t("courses.instructor") || "Instructor"}
                </p>
                <h3 className="text-white text-lg font-bold mt-1 truncate">
                  {data?.name}
                </h3>
              </div>
            </div>

            <div className="flex flex-col gap-3 flex-1 min-w-0">
              {/* Desktop/tablet-only name (mobile renders it beside the avatar above) */}
              <div className="hidden sm:block">
                <p className="text-primary text-xs uppercase tracking-[0.14em] font-semibold">
                  {t("courses.instructor") || "Instructor"}
                </p>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mt-1">
                  {data?.name}
                </h3>
              </div>

              {data?.bio && (
                <p className="text-white/70 text-sm lg:text-base leading-relaxed lg:max-w-2xl">
                  {data.bio}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-2">
                <Link
                  to={`/instructor-profile/${data?.slug}`}
                  className="
                    inline-flex items-center justify-center gap-x-2
                    bg-primary hover:bg-darkPrimary text-white font-semibold
                    rounded-xl px-6 py-3
                    transition-colors
                    shadow-[0_6px_14px_rgba(237,26,77,0.28)]
                  "
                >
                  <span>{t("courses.view_profile")}</span>
                  <ArrowRight size={16} className={isRTL ? "rotate-180" : ""} />
                </Link>

                {socials.length > 0 && (
                  <div className="flex items-center gap-2">
                    {socials.map(({ href, Icon, label }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="
                          w-11 h-11 rounded-xl
                          bg-white/[0.06] border border-white/[0.08]
                          flex items-center justify-center
                          text-white/80 hover:text-primary hover:border-primary/40 hover:bg-white/[0.1]
                          transition-colors
                        "
                      >
                        <Icon size={20} weight="bold" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Instructor;
